/**
 * SuggestionsPanel — shown to tree owners.
 * Lists pending suggestions for a person with approve/reject actions.
 */
import { useEffect, useState } from 'react'
import { api } from '../../services/api.js'

const FIELD_LABELS = {
  first_name: 'All given names', preferred_name: 'Preferred name',
  last_name: 'Last name', birth_name: 'Birth name', gender: 'Gender',
  birth_date: 'Birth date', birth_place: 'Birth place',
  death_date: 'Death date', death_place: 'Death place',
  occupation: 'Occupation', notes: 'Notes',
}

export default function SuggestionsPanel({ treeId, personId, onApproved }) {
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [reviewError, setReviewError] = useState(null)

  useEffect(() => {
    api.get(`/suggestions?tree_id=${treeId}&status=pending`)
      .then(all => setSuggestions(all.filter(s => s.person_id === personId)))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [treeId, personId])

  async function review(id, action) {
    setReviewError(null)
    try {
      await api.patch(`/suggestions/${id}`, { action })
      setSuggestions(prev => prev.filter(s => s.id !== id))
      if (action === 'approve') onApproved()
    } catch (err) {
      setReviewError(err.message || 'Failed to review suggestion')
    }
  }

  if (loading || !suggestions.length) return null

  return (
    <div style={{
      background: 'color-mix(in srgb, var(--gold) 8%, white)',
      border: '1px solid color-mix(in srgb, var(--gold) 30%, white)',
      borderRadius: '10px', padding: '1.25rem', marginTop: '1.5rem'
    }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em',
        textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '1rem' }}>
        Pending suggestions ({suggestions.length})
      </div>

      {reviewError && (
        <div className="form-error" style={{ marginBottom: '0.75rem' }}>
          {reviewError}
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: '0.75rem', padding: '0.15rem 0.5rem' }}
            onClick={() => setReviewError(null)}
          >Dismiss</button>
        </div>
      )}

      {suggestions.map(s => (
        <div key={s.id} style={{
          background: 'white', border: '1px solid var(--border)',
          borderRadius: '8px', padding: '1rem', marginBottom: '0.75rem'
        }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            <strong>{s.suggested_by_name}</strong> suggests changing <strong>{FIELD_LABELS[s.field] || s.field}</strong>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            {s.current_value && (
              <div style={{ fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Current: </span>
                <span style={{ textDecoration: 'line-through', color: '#dc2626' }}>{s.current_value}</span>
              </div>
            )}
            <div style={{ fontSize: '0.82rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Suggested: </span>
              <strong style={{ color: 'var(--green)' }}>{s.suggested_value}</strong>
            </div>
          </div>

          {s.note && (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic',
              marginBottom: '0.5rem' }}>
              "{s.note}"
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button className="btn btn-sm btn-primary" onClick={() => review(s.id, 'approve')}>
              Approve
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => review(s.id, 'reject')}
              style={{ color: '#dc2626' }}>
              Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
